import React, { useState, useEffect } from "react";
import { Alert, Tab, Tabs, Badge, Button, Collapse, Spinner } from "react-bootstrap";
import { formatDateWithTime } from "services/MomentUtils";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import { showNotification } from "services/NotificationService";
import { getErrorMessage } from "services/UtilityService";
import "./AiCallResults.css";

/** Same ordering as the Transcripts / Call Results tabs (newest attempt first). */
const sortCallsByCreationDateDesc = (calls) =>
  [...calls].sort((a, b) => {
    const dateA = a.creation_date ? new Date(a.creation_date) : new Date(0);
    const dateB = b.creation_date ? new Date(b.creation_date) : new Date(0);
    return dateB - dateA;
  });

const AiCallResults = (props) => {
  const { callDetailsList, callGroupId } = props;
  const [expandedAttempts, setExpandedAttempts] = useState({});
  const [transcriptData, setTranscriptData] = useState(null);
  const [loadingTranscripts, setLoadingTranscripts] = useState(false);
  const [activeTab, setActiveTab] = useState("webhook");
  const [fetchAttempted, setFetchAttempted] = useState(false);

  // Reset fetch attempt when tab changes away from transcripts
  useEffect(() => {
    if (activeTab !== "conversationLog") {
      setFetchAttempted(false);
      setTranscriptData(null);
    }
  }, [activeTab]);

  // Reset fetch attempt when callDetailsList changes (different call group)
  useEffect(() => {
    setFetchAttempted(false);
    setTranscriptData(null);
    setLoadingTranscripts(false);
  }, [callDetailsList]);

  // Fetch transcript data when Transcript tab is clicked
  useEffect(() => {
    if (activeTab === "conversationLog" && callDetailsList && callDetailsList.length > 0 && !transcriptData && !loadingTranscripts && !fetchAttempted) {
      // SIDs must match UI row order so response[i] aligns with sortedCalls[i]
      const callSids = sortCallsByCreationDateDesc(callDetailsList)
        .map((call) => call.call_sid)
        .filter((sid) => sid != null);

      if (callSids.length === 0) {
        return;
      }

      setLoadingTranscripts(true);
      setFetchAttempted(true);
      const requestBody = callGroupId != null
        ? { callSids, callGroupId }
        : { callSids };
      sendAuthenticatedAsyncRequest(
        `/agent-workspace/polyai/call-details/transcripts-by-sids`,
        "POST",
        requestBody,
        (r) => {
          setTranscriptData(r.data);
          setLoadingTranscripts(false);
        },
        (e) => {
          setLoadingTranscripts(false);
          showNotification({
            title: "Error",
            message:
              getErrorMessage(e.data) ||
              "Failed to fetch transcript data. Please try again.",
            position: "tr",
            type: "error",
          });
        }
      );
    }
  }, [activeTab, callDetailsList, transcriptData, loadingTranscripts, fetchAttempted, callGroupId]);

  if (!callDetailsList || callDetailsList.length === 0) {
    return (
      <Alert variant="info" className="mb-2">
        No PolyAI call results available.
      </Alert>
    );
  }

  const sortedCalls = sortCallsByCreationDateDesc(callDetailsList);

  const getStatusBadgeVariant = (status) => {
    if (!status) return "secondary";
    const statusUpper = status.toUpperCase();
    if (statusUpper === "SUCCESS" || statusUpper === "COMPLETED") {
      return "success";
    }
    if (statusUpper.includes("FAILED") || statusUpper.includes("ERROR") || statusUpper === "NO_ANSWER") {
      return "danger";
    }
    if (statusUpper.includes("PROCESSING") || statusUpper.includes("SCHEDULED")) {
      return "warning";
    }
    return "info";
  };

  const parseWebhookPayload = (webhookPayload) => {
    if (!webhookPayload) return null;
    try {
      return JSON.parse(webhookPayload);
    } catch (e) {
      return null;
    }
  };

  const toggleAttempt = (index) => {
    setExpandedAttempts((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const renderAttemptHeader = (callDetail, attemptNumber, index) => {
    // Always use scheduling_status for the attempt header
    const displayStatus = callDetail.scheduling_status;
    const isExpanded = expandedAttempts[index] === true; // Default to collapsed
    
    return (
      <div
        className={`attempt-header ${isExpanded ? "expanded" : "collapsed"}`}
        onClick={() => toggleAttempt(index)}
      >
        <div className="attempt-header-content">
          <div className="attempt-header-left">
            <span className="attempt-header-arrow">
              {isExpanded ? "▼" : "▶"}
            </span>
            <div>
              <h5 className="attempt-header-title">
                Attempt {attemptNumber}
              </h5>
              {displayStatus && (
                <Badge bg={getStatusBadgeVariant(displayStatus)} className="attempt-header-badge">
                  {displayStatus}
                </Badge>
              )}
              {callDetail.creation_date && (
                <span className="attempt-header-date">
                  {formatDateWithTime(callDetail.creation_date)}
                </span>
              )}
            </div>
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            {callDetail.call_sid && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() =>
                  window.open(
                    `https://studio.us.poly.ai/razormetrics-us/razormetrics-usp/conversations/${callDetail.call_sid}`,
                    "_blank"
                  )
                }
              >
                View in Agent Studio
              </Button>
            )}
          </div>
        </div>
        {callDetail.call_sid && (
          <div className="attempt-header-call-sid">
            <strong>Call SID:</strong> {callDetail.call_sid}
          </div>
        )}
      </div>
    );
  };

  const renderWebhookInfo = (webhookData, attemptNumber, index) => {
    if (!webhookData) return null;

    return (
      <div>
        <div className="webhook-info-section">
          <h6>Call Status & Summary</h6>
          
          {webhookData.status && (
            <div className="webhook-info-item">
              <strong>Status:</strong>{" "}
              <Badge bg={getStatusBadgeVariant(webhookData.status)} className="webhook-status-badge">
                {webhookData.status}
              </Badge>
            </div>
          )}

          {webhookData.metrics?.call_summary && (
            <div className="webhook-info-item">
              <strong>Call Summary:</strong>
              <div className="call-summary-box">
                {webhookData.metrics.call_summary}
              </div>
            </div>
          )}

          {webhookData.metrics?.human_reached !== undefined && (
            <div className="webhook-info-item">
              <strong>Human Reached:</strong>{" "}
              <Badge bg={webhookData.metrics.human_reached ? "success" : "secondary"}>
                {webhookData.metrics.human_reached ? "Yes" : "No"}
              </Badge>
            </div>
          )}

          {webhookData.metrics?.human_agent_requested !== undefined && (
            <div className="webhook-info-item">
              <strong>Human Agent Requested:</strong>{" "}
              <Badge bg={webhookData.metrics.human_agent_requested ? "warning" : "secondary"}>
                {webhookData.metrics.human_agent_requested ? "Yes" : "No"}
              </Badge>
            </div>
          )}

          {webhookData.metrics?.doctor_metrics && webhookData.metrics.doctor_metrics.length > 0 && (
            <div className="mt-3">
              <strong>Doctor Information:</strong>
              {webhookData.metrics.doctor_metrics.map((doctor, idx) => (
                <div key={idx} className="doctor-info-box">
                  {doctor.doctor_npi && (
                    <div><strong>NPI:</strong> {doctor.doctor_npi}</div>
                  )}
                  {doctor.fax_number && (
                    <div><strong>Fax Number:</strong> {doctor.fax_number}</div>
                  )}
                  {doctor.phone_number && (
                    <div><strong>Phone Number:</strong> {doctor.phone_number}</div>
                  )}
                  {doctor.provider_retired && (
                    <div><strong>Provider Retired:</strong> {doctor.provider_retired}</div>
                  )}
                  {doctor.provider_not_at_office && (
                    <div><strong>Provider Not At Office:</strong> {doctor.provider_not_at_office}</div>
                  )}
                  {doctor.tags && doctor.tags.length > 0 && (
                    <div className="doctor-tags">
                      <strong>Tags:</strong>{" "}
                      {doctor.tags.map((tag, tagIdx) => (
                        <Badge key={tagIdx} bg="info" className="doctor-tag-badge">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {doctor.switches &&
                    Array.isArray(doctor.switches) &&
                    doctor.switches.length > 0 && (
                      <div className="doctor-switch-information">
                        <strong>Switch Information:</strong>
                        {doctor.switches.map((sw, swIdx) => (
                          <div key={swIdx} className="doctor-switch-row">
                            {sw.switch_id != null && sw.switch_id !== "" && (
                              <div className="doctor-switch-id">
                                <strong>Switch ID:</strong>{" "}
                                <span className="text-muted">{sw.switch_id}</span>
                              </div>
                            )}
                            <div className="doctor-tags">
                              <strong>Tags:</strong>{" "}
                              {sw.tags && sw.tags.length > 0 ? (
                                sw.tags.map((tag, tagIdx) => (
                                  <Badge
                                    key={tagIdx}
                                    bg="secondary"
                                    className="doctor-tag-badge"
                                  >
                                    {tag}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-muted small">—</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  /** PolyAI transcripts-by-sids: one entry per requested SID; turns use speaker + text (snake_case transcript_turns). */
  const renderNormalizedTranscript = (turns) => {
    if (!turns || turns.length === 0) {
      return (
        <div className="p-3">
          <Alert variant="info" className="mb-0">
            No conversation turns available for this attempt.
          </Alert>
        </div>
      );
    }

    return (
      <div className="conversation-section">
        <h6>Conversation</h6>
        {turns.map((turn, idx) => {
          const isUser = turn.speaker === "USER";
          // User (caller) left, agent (us) right — swap bubble classes vs speaker name only.
          return (
            <div key={idx} className="conversation-turn">
              <div className={isUser ? "message-container-agent" : "message-container-user"}>
                <div className={isUser ? "message-bubble-agent" : "message-bubble-user"}>
                  <div className="message-label">{isUser ? "User" : "AI Agent"}</div>
                  <div className="message-content">{turn.text}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <Alert variant="info" className="mb-3">
        <strong>{sortedCalls.length}</strong> call attempt{sortedCalls.length !== 1 ? "s" : ""} found.
        {sortedCalls.length > 1 && " Results are ordered with the most recent attempt first."}
      </Alert>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        id="polyai-call-results-tabs"
        className="mb-3"
      >
        <Tab eventKey="webhook" title="Call Results">
          {sortedCalls.map((callDetail, index) => {
            const attemptNumber = sortedCalls.length - index;
            const webhookData = parseWebhookPayload(callDetail.webhook_raw_payload);
            const isExpanded = expandedAttempts[index] === true;
            
            return (
              <div key={callDetail.call_sid || index} className="attempt-container">
                {renderAttemptHeader(callDetail, attemptNumber, index)}
                <Collapse in={isExpanded}>
                  <div>
                    {webhookData ? (
                      renderWebhookInfo(webhookData, attemptNumber, index)
                    ) : (
                      <div className="p-3">
                        <Alert variant="warning" className="mb-0">
                          Call results are not available for Attempt {attemptNumber}.
                        </Alert>
                      </div>
                    )}
                  </div>
                </Collapse>
              </div>
            );
          })}
        </Tab>
        <Tab eventKey="conversationLog" title="Transcripts">
          {loadingTranscripts ? (
            <div className="p-3 text-center">
              <Spinner animation="border" role="status" className="mb-2">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <div>Loading transcript data...</div>
            </div>
          ) : (
            sortedCalls.map((callDetail, index) => {
              const attemptNumber = sortedCalls.length - index;
              const entry = transcriptData && transcriptData.length > index ? transcriptData[index] : null;
              const turns = entry?.transcript_turns;
              const isExpanded = expandedAttempts[index] === true;

              return (
                <div key={callDetail.call_sid || index} className="attempt-container">
                  {renderAttemptHeader(callDetail, attemptNumber, index)}
                  <Collapse in={isExpanded}>
                    <div>
                      {transcriptData == null ? (
                        <div className="p-3">
                          <Alert variant="warning" className="mb-0">
                            No transcript available for Attempt {attemptNumber}.
                          </Alert>
                        </div>
                      ) : (
                        renderNormalizedTranscript(Array.isArray(turns) ? turns : [])
                      )}
                    </div>
                  </Collapse>
                </div>
              );
            })
          )}
        </Tab>
      </Tabs>
    </div>
  );
};

export default AiCallResults;
